'use client';

import { useContext } from 'react';
import { I18nContext } from 'react-i18next';
import { i18nEn } from '@/i18n';

/**
 * Rótulo del spinner para lectores de pantalla. EE.UU. en inglés: se sabe por la
 * instancia de i18n que el servidor le da al sitio por país (`I18nProvider` →
 * `i18nEn`), que ya está bien desde el HTML del servidor. Cualquier otro sitio,
 * el "Cargando" de siempre (el visitante chileno en inglés también).
 */
export function useRotuloCargando(): string {
  const contexto = useContext(I18nContext);
  return contexto?.i18n === i18nEn ? 'Loading' : 'Cargando';
}

/**
 * Lo que se ve mientras una página termina de llegar.
 *
 * Existía ya dentro de `BuilderPageWrapper`, para el rato en que la estructura
 * del builder todavía no está en memoria. Ahora además es el `loading.tsx` de
 * cada ruta del menú, y por eso vive acá: si fueran dos spinners distintos, al
 * navegar se vería uno, después el otro, y el salto entre ambos se leería como
 * un parpadeo.
 *
 * Por qué un spinner y no un esqueleto de contenido: cada automotora arma sus
 * páginas en el builder, así que no hay una silueta que sea cierta para todas.
 * Un esqueleto con la forma equivocada miente sobre lo que está por aparecer.
 *
 * `min-h-screen` no es decoración: sin un alto que ocupe la pantalla el
 * documento se encoge durante la transición y el scroll pega un salto.
 *
 * El `role="status"` es lo que hace que un lector de pantalla anuncie la espera
 * —y, de paso, lo que mide el harness de `scripts/navegacionMenuHarness`.
 */
export default function PageLoading() {
  const rotulo = useRotuloCargando();
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div
        className="h-9 w-9 rounded-full border-2 border-current border-t-transparent animate-spin text-gray-300 dark:text-neutral-700"
        role="status"
        aria-label={rotulo}
      />
    </div>
  );
}
